import React, { useState, useEffect } from 'react';
import {
    Settings as SettingsIcon,
    Shield,
    Bell,
    Monitor,
    Wifi,
    Database,
    Save,
    RefreshCw,
    Check,
    AlertCircle,
    Users,
    Activity,
    Layers,
    Terminal,
    Zap,
    Cpu,
    Lock
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { apiService } from '../services/api';
import { Management } from './Management';
import DeviceManager from './DeviceManager';
import { motion, AnimatePresence } from 'framer-motion';

// --- Shared Glass Components ---

const GlassPanel: React.FC<{ children: React.ReactNode; className?: string; title?: string; icon?: any; description?: string }> = ({ children, className = '', title, icon: Icon, description }) => (
  <div className={`bg-white/5 backdrop-blur-2xl border border-white/10 rounded-2xl overflow-hidden shadow-2xl ${className}`}>
    {(title || Icon || description) && (
      <div className="px-6 py-5 border-b border-white/5 bg-white/[0.02]">
        <div className="flex items-center gap-3 mb-2">
          {Icon && <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center border border-blue-500/20"><Icon className="w-4 h-4 text-blue-400" /></div>}
          {title && <h3 className="text-sm font-black tracking-tight text-white uppercase">{title}</h3>}
        </div>
        {description && <p className="text-[10px] text-blue-100/40 font-light tracking-wide italic">{description}</p>}
      </div>
    )}
    <div className="p-6">
      {children}
    </div>
  </div>
);

const SettingToggle: React.FC<{ label: string; description: string; isActive: boolean; onToggle: () => void }> = ({ label, description, isActive, onToggle }) => (
  <div className="flex items-center justify-between p-5 bg-white/[0.03] border border-white/5 hover:border-blue-500/30 transition-all rounded-xl cursor-pointer group" onClick={onToggle}>
    <div>
      <h4 className="text-xs font-bold text-white uppercase tracking-tight mb-1 group-hover:text-blue-400 transition-colors">{label}</h4>
      <p className="text-[9px] text-blue-100/40 uppercase tracking-widest leading-relaxed">{description}</p>
    </div>
    <div className={`w-12 h-6 rounded-full transition-all flex items-center px-1 flex-shrink-0 ${isActive ? 'bg-blue-600 shadow-[0_0_15px_rgba(37,99,235,0.4)]' : 'bg-white/10'}`}>
      <motion.div 
        layout
        className={`w-4 h-4 rounded-full bg-white transition-all`}
        animate={{ x: isActive ? 24 : 0 }}
        transition={{ type: "spring", stiffness: 500, damping: 30 }}
      />
    </div>
  </div>
);

const SettingSelect: React.FC<{ label: string; description: string; value: string; options: {value: string, label: string}[]; onChange: (v: string) => void }> = ({ label, description, value, options, onChange }) => (
  <div className="p-5 bg-white/[0.03] border border-white/5 rounded-xl">
    <h4 className="text-xs font-bold text-white uppercase tracking-tight mb-1">{label}</h4>
    <p className="text-[9px] text-blue-100/40 uppercase tracking-widest mb-4 leading-relaxed">{description}</p>
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full appearance-none bg-black/40 border border-white/10 rounded-lg py-3 px-4 text-xs font-black text-blue-400 uppercase tracking-widest focus:outline-none focus:border-blue-500/50 transition-colors"
      >
        {options.map(opt => <option key={opt.value} value={opt.value} className="bg-[#0f111a] text-white">{opt.label}</option>)}
      </select>
      <div className="absolute inset-y-0 right-4 flex items-center pointer-events-none">
         <SettingsIcon className="w-4 h-4 text-blue-500/40" />
      </div>
    </div>
  </div>
);

const SettingInput: React.FC<{ label: string; description: string; value: string | number; type?: string; onChange: (v: string) => void }> = ({ label, description, value, type = "text", onChange }) => (
  <div className="p-5 bg-white/[0.03] border border-white/5 rounded-xl">
    <h4 className="text-xs font-bold text-white uppercase tracking-tight mb-1">{label}</h4>
    <p className="text-[9px] text-blue-100/40 uppercase tracking-widest mb-4 leading-relaxed">{description}</p>
    <div className="relative">
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-black/40 border border-white/10 rounded-lg py-3 px-4 text-xs font-black text-white placeholder-white/20 uppercase tracking-widest focus:outline-none focus:border-blue-500/50 transition-colors"
      />
      <div className="absolute inset-y-0 right-4 flex items-center pointer-events-none">
         <Terminal className="w-4 h-4 text-blue-500/40" />
      </div>
    </div>
  </div>
);

export function Settings({ setupData, isSuperAdmin }: { setupData?: any; isSuperAdmin?: boolean }) {
    const [activeSection, setActiveSection] = useState('general');
    const [isSaving, setIsSaving] = useState(false);
    const [saveStatus, setSaveStatus] = useState<{ success?: boolean; message?: string }>({});
    const [archiveStatus, setArchiveStatus] = useState<{ loading?: boolean; success?: boolean; message?: string }>({});

    const [systemSettings, setSystemSettings] = useState({
        autoSave: true,
        notifications: true,
        highQuality: false,
        alertFrequency: 'medium',
        emailNotifications: true,
        smsAlerts: false,
        userDetailDisplay: true,
        autoClearResolved: true,
        criticalAlertSounds: true,
        preferEdge: false,
        soundVolume: 50,
        emailRecipients: ['admin@csense.com'],
        smsNumbers: ['+1234567890'],
        alertPriority: 'medium',
        notificationDelay: 0,
        quietHours: false,
        quietStart: '22:00',
        quietEnd: '07:00',
        retryAttempts: 3,
        webhookUrl: '',
        testMode: false
    });

    useEffect(() => {
        const savedSettings = localStorage.getItem('csense-settings');
        if (savedSettings) {
            try { setSystemSettings(JSON.parse(savedSettings)); } catch (e) {}
        }
    }, []);

    const handleSettingChange = (setting: keyof typeof systemSettings, value: any) => {
        setSystemSettings(prev => ({ ...prev, [setting]: value }));
    };

    const saveSettings = async () => {
        setIsSaving(true);
        setSaveStatus({});
        try {
            localStorage.setItem('csense-settings', JSON.stringify(systemSettings));
            setSaveStatus({ success: true, message: 'Protocol Configuration Synchronized.' });
            setTimeout(() => setSaveStatus({}), 4000);
        } catch (error) {
            setSaveStatus({ success: false, message: 'Configuration sync failure.' });
        } finally {
            setIsSaving(false);
        }
    };

    const archiveAndClearNotifications = async () => {
        if (!window.confirm('This will export all current notifications to a server-side tar backup, then permanently clear them from the live dashboard. Continue?')) {
            return;
        }
        setArchiveStatus({ loading: true });
        try {
            const token = localStorage.getItem('csense_auth_token');
            const response = await fetch('/api/admin/notifications/archive-and-clear', {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (response.ok) {
                setArchiveStatus({
                    success: true,
                    message: `Archived ${data.exported_count} notification(s) to ${data.backup_file} and cleared the live feed.`
                });
            } else {
                setArchiveStatus({ success: false, message: data.detail || 'Archive failed.' });
            }
        } catch (error) {
            setArchiveStatus({ success: false, message: 'Archive request failed.' });
        } finally {
            setArchiveStatus(prev => ({ ...prev, loading: false }));
        }
    };

    const resetSettings = () => {
        setSystemSettings({
            autoSave: true,
            notifications: true,
            highQuality: false,
            alertFrequency: 'medium',
            emailNotifications: true,
            smsAlerts: false,
            userDetailDisplay: true,
            autoClearResolved: true,
            criticalAlertSounds: true,
            preferEdge: false,
            soundVolume: 50,
            emailRecipients: ['admin@csense.com'],
            smsNumbers: ['+1234567890'],
            alertPriority: 'medium',
            notificationDelay: 0,
            quietHours: false,
            quietStart: '22:00',
            quietEnd: '07:00',
            retryAttempts: 3,
            webhookUrl: '',
            testMode: false
        });
    };

    const sections = [
        { id: 'general', title: 'Core Protocols', sub: 'System Defaults', icon: Cpu },
        { id: 'management', title: 'Entity Matrix', sub: 'Users & Org', icon: Users },
        { id: 'security', title: 'Threat Thresholds', sub: 'Alert Levels', icon: Shield },
        { id: 'notifications', title: 'Triage Routing', sub: 'Push & Email', icon: Bell },
        { id: 'display', title: 'Visual Engine', sub: 'Stream Quality', icon: Monitor },
        { id: 'network', title: 'Neural Mesh', sub: 'Nodes & Uplink', icon: Wifi },
        { id: 'storage', title: 'Archive Vault', sub: 'Retention Data', icon: Database }
    ];

    const renderGeneralSettings = () => (
        <div className="space-y-6">
            <GlassPanel title="Protocol Configuration" icon={SettingsIcon} description="Base operational behaviors for the C.S.E.N.S.E environment.">
                <div className="space-y-3">
                    <SettingToggle 
                        label="Auto-Sync Protocols" 
                        description="Automatically push local changes to the global state immediately." 
                        isActive={systemSettings.autoSave} 
                        onToggle={() => handleSettingChange('autoSave', !systemSettings.autoSave)} 
                    />
                    <SettingToggle 
                        label="Prioritize Edge Nodes" 
                        description="Offload neural processing to local hardware over central cloud clusters." 
                        isActive={systemSettings.preferEdge} 
                        onToggle={() => {
                            handleSettingChange('preferEdge', !systemSettings.preferEdge);
                            localStorage.setItem('csense-prefer-edge', (!systemSettings.preferEdge).toString());
                            window.dispatchEvent(new CustomEvent('preferEdgeChanged', { detail: { preferEdge: !systemSettings.preferEdge } }));
                        }} 
                    />
                    <SettingToggle 
                        label="Diagnostic Logging" 
                        description="Enable deep telemetry for advanced backend diagnostics." 
                        isActive={systemSettings.testMode} 
                        onToggle={() => handleSettingChange('testMode', !systemSettings.testMode)} 
                    />
                </div>
            </GlassPanel>
        </div>
    );

    const renderSecuritySettings = () => (
        <div className="space-y-6">
            <GlassPanel title="Threat Evaluation Layer" icon={Lock} description="Define how the AI categorizes and broadcasts detected anomalies.">
                <div className="space-y-4">
                    <SettingSelect 
                        label="Anomaly Broadcasting"
                        description="Determine the noise floor for active alert generation."
                        value={systemSettings.alertFrequency}
                        onChange={(v) => handleSettingChange('alertFrequency', v)}
                        options={[
                           { value: 'low', label: 'Critical Deviations Only' },
                           { value: 'medium', label: 'Standard Operational Matrix' },
                           { value: 'high', label: 'Unfiltered Raw Stream' }
                        ]}
                    />
                    <SettingSelect 
                        label="Base Action Priority"
                        description="Default severity assigned to unidentified deviations."
                        value={systemSettings.alertPriority}
                        onChange={(v) => handleSettingChange('alertPriority', v)}
                        options={[
                           { value: 'low', label: 'Monitor Only' },
                           { value: 'medium', label: 'Standard Flag' },
                           { value: 'high', label: 'Tactical Review' },
                           { value: 'critical', label: 'Immediate Action Required' }
                        ]}
                    />
                </div>
            </GlassPanel>
        </div>
    );

    const renderNotificationSettings = () => (
        <div className="space-y-6">
            <GlassPanel title="Targeted Forwarding" icon={Activity} description="Configure where intelligence is routed upon detection.">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <SettingToggle 
                        label="Browser HUD Push" 
                        description="Direct visual overlays in the active session." 
                        isActive={systemSettings.notifications} 
                        onToggle={() => handleSettingChange('notifications', !systemSettings.notifications)} 
                    />
                    <SettingToggle 
                        label="Email Dispatch" 
                        description="Forward intelligence summaries via secure SMTP." 
                        isActive={systemSettings.emailNotifications} 
                        onToggle={() => handleSettingChange('emailNotifications', !systemSettings.emailNotifications)} 
                    />
                    <SettingToggle 
                        label="Tactical SMS Relay" 
                        description="Immediate cellular alerts for critical breaches." 
                        isActive={systemSettings.smsAlerts} 
                        onToggle={() => handleSettingChange('smsAlerts', !systemSettings.smsAlerts)} 
                    />
                    <SettingToggle 
                        label="Auditory Cues" 
                        description="Haptic sound feedback on critical event streams." 
                        isActive={systemSettings.criticalAlertSounds} 
                        onToggle={() => handleSettingChange('criticalAlertSounds', !systemSettings.criticalAlertSounds)} 
                    />
                </div>
            </GlassPanel>

            <GlassPanel title="Routing Constraints" icon={Layers}>
                <div className="space-y-4">
                    <div className="flex flex-col md:flex-row gap-4">
                       <div className="flex-1">
                          <SettingInput 
                              label="Transmission Delay" 
                              description="Seconds to withhold before broadcasting." 
                              type="number" 
                              value={systemSettings.notificationDelay} 
                              onChange={(v) => handleSettingChange('notificationDelay', parseInt(v) || 0)} 
                          />
                       </div>
                       <div className="flex-1">
                          <SettingInput 
                              label="Maximum Retries" 
                              description="Packet resend limits on failed relays." 
                              type="number" 
                              value={systemSettings.retryAttempts} 
                              onChange={(v) => handleSettingChange('retryAttempts', parseInt(v) || 1)} 
                          />
                       </div>
                    </div>
                    
                    <div className="p-5 bg-white/[0.03] border border-white/5 rounded-xl">
                       <div className="flex items-center justify-between mb-4">
                          <div>
                             <h4 className="text-xs font-bold text-white uppercase tracking-tight mb-1">Stealth Window (Quiet Hours)</h4>
                             <p className="text-[9px] text-blue-100/40 uppercase tracking-widest leading-relaxed">Suppress non-critical routing during off-peak times.</p>
                          </div>
                          <div className={`w-12 h-6 rounded-full transition-all flex items-center px-1 flex-shrink-0 cursor-pointer ${systemSettings.quietHours ? 'bg-blue-600' : 'bg-white/10'}`} onClick={() => handleSettingChange('quietHours', !systemSettings.quietHours)}>
                            <motion.div layout className={`w-4 h-4 rounded-full bg-white transition-all`} animate={{ x: systemSettings.quietHours ? 24 : 0 }} />
                          </div>
                       </div>
                       {systemSettings.quietHours && (
                          <div className="grid grid-cols-2 gap-4 mt-4 py-4 border-t border-white/5">
                             <div>
                                <label className="text-[9px] font-black uppercase text-blue-400 tracking-widest block mb-2">Engage Stealth</label>
                                <input type="time" value={systemSettings.quietStart} onChange={(e) => handleSettingChange('quietStart', e.target.value)} className="w-full bg-black/40 border border-white/10 rounded-lg p-3 text-xs text-white" />
                             </div>
                             <div>
                                <label className="text-[9px] font-black uppercase text-blue-400 tracking-widest block mb-2">Disengage Stealth</label>
                                <input type="time" value={systemSettings.quietEnd} onChange={(e) => handleSettingChange('quietEnd', e.target.value)} className="w-full bg-black/40 border border-white/10 rounded-lg p-3 text-xs text-white" />
                             </div>
                          </div>
                       )}
                    </div>
                </div>
            </GlassPanel>

            <DeviceManager />
        </div>
    );

    const renderDisplaySettings = () => (
        <div className="space-y-6">
            <GlassPanel title="Visual Optics" icon={Monitor} description="Control camera streaming resolution and rendering engine behaviors.">
                <div className="space-y-4">
                    <SettingToggle 
                        label="Full-Fidelity Analytics" 
                        description="Force uncompressed video streams. Increases bandwidth loads." 
                        isActive={systemSettings.highQuality} 
                        onToggle={() => handleSettingChange('highQuality', !systemSettings.highQuality)} 
                    />
                    <SettingToggle 
                        label="Render Entity Overlay" 
                        description="Draw bounding boxes and confidence scores over live feeds." 
                        isActive={systemSettings.userDetailDisplay} 
                        onToggle={() => handleSettingChange('userDetailDisplay', !systemSettings.userDetailDisplay)} 
                    />
                </div>
            </GlassPanel>
        </div>
    );

    const renderNetworkSettings = () => (
        <div className="space-y-6">
            <GlassPanel title="Neural Uplink Status" icon={Wifi} description="Current grid connection health and camera array status.">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="p-6 bg-white/[0.03] border border-white/5 rounded-xl flex items-center gap-6">
                       <div className="relative">
                          <div className="w-12 h-12 rounded-full border border-emerald-500/30 flex items-center justify-center bg-emerald-500/10">
                             <Wifi className="w-5 h-5 text-emerald-400" />
                          </div>
                          <div className="absolute top-0 right-0 w-3 h-3 bg-emerald-500 rounded-full animate-pulse border-2 border-[#0a0b14]" />
                       </div>
                       <div>
                          <p className="text-[10px] font-black text-blue-100/40 uppercase tracking-widest">Main Server</p>
                          <p className="text-xl font-bold text-white mt-1">CONNECTED</p>
                       </div>
                    </div>

                    <div className="p-6 bg-white/[0.03] border border-white/5 rounded-xl flex items-center gap-6">
                       <div className="w-12 h-12 rounded-full border border-blue-500/30 flex items-center justify-center bg-blue-500/10">
                          <Zap className="w-5 h-5 text-blue-400" />
                       </div>
                       <div>
                          <p className="text-[10px] font-black text-blue-100/40 uppercase tracking-widest">Active Nodes</p>
                          <p className="text-xl font-bold text-white mt-1">{setupData?.cameras?.length || 0} DEPLOYED</p>
                       </div>
                    </div>
                </div>
            </GlassPanel>
        </div>
    );

    const renderStorageSettings = () => (
        <div className="space-y-6">
            <GlassPanel title="Data Retention Policies" icon={Database} description="Global configuration for archive deletion and artifact lifespans.">
                <div className="space-y-4">
                    <SettingToggle 
                        label="Garbage Collection" 
                        description="Automatically purge resolved anomalies after 7 operational cycles." 
                        isActive={systemSettings.autoClearResolved} 
                        onToggle={() => handleSettingChange('autoClearResolved', !systemSettings.autoClearResolved)} 
                    />
                    
                    <div className="p-6 bg-blue-900/10 border border-blue-500/20 rounded-xl mt-4">
                        <div className="flex items-center gap-3 mb-2">
                           <Activity className="w-4 h-4 text-blue-400" />
                           <h4 className="text-xs font-black text-white uppercase tracking-widest">Archive Footprint</h4>
                        </div>
                        <div className="w-full h-2 bg-black/40 rounded-full my-4 overflow-hidden border border-white/5">
                           <div className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 w-[65%] shadow-[0_0_10px_rgba(59,130,246,0.5)]" />
                        </div>
                        <div className="flex justify-between text-[10px] font-black uppercase text-blue-100/40 tracking-widest">
                           <span>1.2TB Consumed</span>
                           <span>2TB Capacity (65%)</span>
                        </div>
                    </div>
                </div>
            </GlassPanel>

            {isSuperAdmin && (
                <GlassPanel title="Notification Archive" icon={Bell} description="Super-admin only: back up the live notification feed before clearing it.">
                    <div className="space-y-4">
                        <p className="text-[10px] text-blue-100/40 leading-relaxed uppercase tracking-widest">
                            Exports every notification currently in the system to a tar backup on the server, then clears the live feed for all clients.
                        </p>
                        <Button
                            onClick={archiveAndClearNotifications}
                            disabled={archiveStatus.loading}
                            className="bg-red-600/80 hover:bg-red-600 text-white font-black uppercase text-[10px] tracking-widest px-6 py-5 rounded-xl"
                        >
                            {archiveStatus.loading ? 'Archiving...' : 'Archive & Clear All Notifications'}
                        </Button>
                        {archiveStatus.message && (
                            <div className={`p-4 rounded-xl text-[10px] uppercase tracking-widest font-bold ${archiveStatus.success ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-red-500/10 text-red-400 border border-red-500/20'}`}>
                                {archiveStatus.message}
                            </div>
                        )}
                    </div>
                </GlassPanel>
            )}
        </div>
    );

    const renderManagementSettings = () => (
        <div className="space-y-6">
            <Management />
        </div>
    );

    const renderContent = () => {
        switch (activeSection) {
            case 'general': return renderGeneralSettings();
            case 'management': return renderManagementSettings();
            case 'security': return renderSecuritySettings();
            case 'notifications': return renderNotificationSettings();
            case 'display': return renderDisplaySettings();
            case 'network': return renderNetworkSettings();
            case 'storage': return renderStorageSettings();
            default: return renderGeneralSettings();
        }
    };

    return (
        <div className="min-h-screen bg-[#0a0b14] text-white p-6 pb-32 space-y-8">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 border-b border-white/5 pb-8 relative z-10">
                <div>
                    <h1 className="text-3xl font-black tracking-tighter uppercase text-white flex items-center gap-3">
                       <SettingsIcon className="w-8 h-8 text-blue-500 opacity-50" />
                       System Matrix
                    </h1>
                    <p className="text-[10px] font-black text-blue-100/40 mt-2 uppercase tracking-[0.2em]">
                       Global parameters and operational engine policies
                    </p>
                </div>
                
                <div className="flex items-center gap-3">
                   <Button variant="ghost" onClick={resetSettings} className="text-red-400 hover:bg-red-500/10 hover:text-red-300 font-black text-[10px] uppercase tracking-widest px-6 py-5 rounded-2xl transition-all border border-red-500/20">
                      <RefreshCw className="w-3.5 h-3.5 mr-2" /> Reset Engine
                   </Button>
                   <Button onClick={saveSettings} disabled={isSaving} className="bg-blue-600 hover:bg-blue-500 text-white font-black text-[10px] uppercase tracking-widest px-8 py-5 rounded-2xl transition-all shadow-[0_0_30px_rgba(37,99,235,0.3)]">
                      {isSaving ? <><RefreshCw className="w-3.5 h-3.5 mr-2 animate-spin" /> SYNCHRONIZING</> : <><Save className="w-3.5 h-3.5 mr-2" /> APPLY CHANGES</>}
                   </Button>
                </div>
            </div>
            
            <AnimatePresence>
              {saveStatus.message && (
                 <motion.div 
                   initial={{ opacity: 0, y: -20, scale: 0.95 }}
                   animate={{ opacity: 1, y: 0, scale: 1 }}
                   exit={{ opacity: 0, scale: 0.95 }}
                   className={`p-4 rounded-xl border flex items-center gap-3 mb-8 relative z-10 ${saveStatus.success ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400' : 'bg-red-500/10 border-red-500/30 text-red-400'}`}
                 >
                    {saveStatus.success ? <Check className="w-5 h-5 flex-shrink-0" /> : <AlertCircle className="w-5 h-5 flex-shrink-0" />}
                    <span className="text-[11px] font-black uppercase tracking-widest">{saveStatus.message}</span>
                 </motion.div>
              )}
            </AnimatePresence>

            <div className="grid grid-cols-1 xl:grid-cols-4 gap-8 relative z-10">
                {/* Protocol Navigation */}
                <div className="xl:col-span-1">
                    <div className="bg-white/5 border border-white/10 rounded-2xl p-3 backdrop-blur-xl sticky top-28 shadow-2xl">
                        <h2 className="text-[10px] font-black text-white/40 uppercase tracking-[0.2em] px-4 py-3 border-b border-white/5 mb-3">Protocol Sectors</h2>
                        <nav className="space-y-1">
                            {sections.map((section) => (
                                <button
                                    key={section.id}
                                    onClick={() => setActiveSection(section.id)}
                                    className={`w-full flex items-center gap-4 px-4 py-3.5 rounded-xl transition-all text-left group ${activeSection === section.id
                                        ? 'bg-blue-600/20 border-l-2 border-blue-500 text-blue-400 shadow-[inset_0_0_20px_rgba(37,99,235,0.1)]'
                                        : 'text-blue-100/40 hover:text-white hover:bg-white/5 border-l-2 border-transparent'
                                    }`}
                                >
                                    <section.icon className={`w-4 h-4 transition-transform ${activeSection === section.id ? 'scale-110' : 'group-hover:scale-110'}`} />
                                    <div className="flex flex-col">
                                       <span className="text-[11px] font-black uppercase tracking-widest">{section.title}</span>
                                       <span className="text-[8px] opacity-60 uppercase tracking-widest mt-0.5">{section.sub}</span>
                                    </div>
                                </button>
                            ))}
                        </nav>
                    </div>
                </div>

                {/* Protocol Display Area */}
                <div className="xl:col-span-3">
                    <AnimatePresence mode="wait">
                       <motion.div
                          key={activeSection}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          transition={{ duration: 0.2 }}
                       >
                          {renderContent()}
                       </motion.div>
                    </AnimatePresence>
                </div>
            </div>
            
            {/* Ambient Lighting */}
            <div className="fixed inset-0 pointer-events-none z-0">
               <div className="absolute top-1/2 left-1/4 w-[600px] h-[600px] bg-blue-600/5 rounded-full blur-[150px] -translate-x-1/2 -translate-y-1/2" />
               <div className="absolute top-1/4 right-0 w-[500px] h-[500px] bg-purple-600/5 rounded-full blur-[120px]" />
            </div>
        </div>
    );
}

export default Settings;
